(ns app.front.ui.column-select
  (:require [app.front.util :as futil]
            [core.front.carbon :as cc]
            [core.front.icons :as i]
            [core.util.util :as util]))

(defn- get-icon [icon-type selected?]
  (let [icons  {:checkbox {:off i/checkbox-off
                           :on  i/check}
                :radio    {:off i/radio-off
                           :on  i/radio-on}}
        on-off (if selected? :on :off)]
    (-> icons icon-type on-off)))

(defn- column-select-option
  "A single option item for the `column-select` component.

  Props:
     tr           tr
     id           string, unique key
     tr-key       string, tr key
     title        string, optionally the title (overrides tr-key if both present)
     value        string, an optional value that's shown in parens after the option title (e.g. Heating (2))
     selected?    boolean, whether this option is selected or not
     disabled?    boolean, whether this option is disabled or not
     transparent? boolean, whether this option is transparent or not
     light?       boolean, whether this component should use light colors
     on-select    function, called with id when user clicks the option
     content      hiccup, overrides title, tr-key and value (for adding edit, delete or other functions)"
  [{:keys [id tr-key title value content selected? tr on-select disabled? transparent? light? data-cy icon-type]}]
  (let [selectable (and (not disabled?) on-select)]
    [:div.px-2.py-1.block.w-full.border-transparent.bg-transparent.text-sm.leading-snug.text-left.cursor-pointer.focus:outline-none.focus:border.focus:border-ui-night-sky-100.border-solid.border
     {:role          "option"
      :tabIndex      0
      :aria-selected (boolean selected?)
      :class         [(when value "font-bold")
                      (when selected? (if light? "bg-ui-blue-10" "bg-ui-night-sky-100 text-white"))
                      (when-not transparent? "px-3")
                      (when (and selected? disabled?) "opacity-50")]
      :on-click      (when selectable #(on-select id))
      :on-key-down   (when selectable (fn [e] (when (#{"Enter" " "} (.-key e)) (on-select id))))
      :data-cy       data-cy}
     (when icon-type
       [:span.inline-block.mr-3.relative.top-0_5
        (cc/icon {:icon        (get-icon icon-type selected?)
                  :height      "1.1em"
                  :description (tr [:select/select])
                  :aria-hidden true
                  :fill        (if selected? "white" "currentcolor")})])
     (or content
         (str (if title title (tr [tr-key]))
              (when value
                (str " (" value ")"))))]))


(defn column-select
  "Renders a box of items for selecting one. Quite like a <select>, but
  with all options visible at once. Used e.g. in Fees management to navigate
  the category - work type - work target tree.

  Props:
    options      - The list of options to show: `{:id        string, unique key
                                                  :tr-key    string, tr key
                                                  :title     string, optionally the title (overrides tr-key if both present)
                                                  :value     string, an optional value that's shown in parens after the option title (e.g. Heating (2))
                                                  :content   hiccup, an optional DOM node that replaces the title, tr key and value
                                                  :selected? boolean, whether the option is selected or not}`
    on-change    - A fn that gets called with the `key` prop of the selected option
    on-edit      - A fn
    tr           - tr
    title        - keyword, a tr key for the title
    height       - CSS string, override for the fixed height
    max-height   - CSS string (default auto)
    transparent? - boolean, whether to render bg color or not (default false)
    light?       - boolean, whether the bg color should be white and other colors matching that (default false)
    disabled?    - boolean, if set will ignore clicks and display with lowered opacity (default false)
    empty-label  - keyword, a tr key for the text shown when there are no options
    icon-type    - keyword (:checkbox/:radio), show icons the beginning of the row. NOTE! :checkbox should only be used
                   when multiple options can be selected and :radio when only one can be selected
    actions      - If present, buttons are shown on top of the component. A collection of maps: `{:tr-key   string, tr key
                                                                                                   :title    string, optionally the title (overrides tr-key)
                                                                                                   :icon     icon, optional
                                                                                                   :on-click function, the callback to call with 0 args when clicked
                                                                                                   :component if given, renders this and disregards other options}`"
  [{:keys [options on-change tr title height max-height actions transparent? light? disabled? empty-label icon-type]}]
  [:div.flex.flex-col {:class (futil/cs (when disabled? "opacity-75"))}
   [:div.min-h-8.mb-1.flex.justify-between.items-end {:class (when transparent? "pb-1 mb-2 border-b border-ui-gray border-solid")}
    [:div.pb-1.bold
     {:class-name  (if disabled? "text-gray-400" "")
      :aria-hidden true}
     (tr [title])]
    (when (seq actions)
      [:div.flex.flex-wrap.gap-1.items-center.justify-end
       (futil/for-frag [action-item (remove nil? actions)]
         (if-let [component (:component action-item)]
           component
           (cc/button
             (merge (select-keys action-item [:icon :on-click])
                    {:size     :small
                     :kind     :ghost
                     :disabled (boolean disabled?)})
             (if (:title action-item)
               (:title action-item)
               (tr [(:tr-key action-item)])))))])]
   (if (and (empty? options)
            (some? empty-label))
     (tr [empty-label])
     [:div.rounded-md.overflow-y-scroll
      {:class      (when-not transparent?
                     ["py-1 " (if light? "bg-white" "bg-gray-200")])
       :role       "listbox"
       :aria-label (tr [title])
       :style      {:height     (or height "350px")
                    :max-height (or max-height "auto")}}
      (futil/for-frag [option-props options]
        [column-select-option (merge option-props
                                     {:on-select on-change}
                                     (util/mmap tr transparent? light? disabled? icon-type))])])])
